import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Rocket, MagnifyingGlass, ClockCounterClockwise, WarningCircle, CheckCircle,
  Buildings, TrendUp, FileText, ArrowRight, Funnel, Users, XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGE_WORKFLOW = [
  "New Application", "Documents Pending", "Documents Verified", "Payment Pending", "Payment Received",
  "Application Submitted", "Government Processing", "Approved", "Certificate Ready", "Delivered", "Completed",
];
const ALL_STAGES = [...STAGE_WORKFLOW, "Rejected"];

const stageColor = (s) => {
  if (/Reject/i.test(s)) return "bg-red-100 text-red-700 border-red-200";
  if (/Completed|Delivered/i.test(s)) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (/Approved|Certificate/i.test(s)) return "bg-blue-100 text-blue-700 border-blue-200";
  if (/Processing|Submitted/i.test(s)) return "bg-amber-100 text-amber-700 border-amber-200";
  if (/Pending/i.test(s)) return "bg-orange-100 text-orange-700 border-orange-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

const KPIS = [
  { key: "today",           label: "Today's Applications", icon: FileText,             tone: "text-blue-700",     bg: "bg-blue-50" },
  { key: "pending_docs",    label: "Documents Pending",    icon: WarningCircle,        tone: "text-orange-700",   bg: "bg-orange-50" },
  { key: "payment_pending", label: "Payment Pending",      icon: WarningCircle,        tone: "text-amber-700",    bg: "bg-amber-50" },
  { key: "govt_processing", label: "Govt Processing",      icon: ClockCounterClockwise, tone: "text-indigo-700",  bg: "bg-indigo-50" },
  { key: "approved",        label: "Approved / Ready",     icon: CheckCircle,          tone: "text-blue-700",     bg: "bg-blue-50" },
  { key: "completed",       label: "Completed",            icon: CheckCircle,          tone: "text-emerald-700",  bg: "bg-emerald-50" },
  { key: "delayed",         label: "Delayed",              icon: XCircle,              tone: "text-red-700",      bg: "bg-red-50" },
];

export default function TrackingDashboard() {
  const nav = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [perStage, setPerStage] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);

  const [stageF, setStageF] = useState("All");
  const [priority, setPriority] = useState("All");
  const [q, setQ] = useState("");
  const [delayedOnly, setDelayedOnly] = useState(false);

  const loadDashboard = async () => {
    try {
      const { data } = await api.get("/tracking/dashboard");
      setKpi(data.kpi); setPerStage(data.per_stage || []);
    } catch (e) { toast.error("Failed to load dashboard"); }
  };
  const loadApps = async () => {
    setLoading(true);
    try {
      const params = {};
      if (priority !== "All") params.priority = priority;
      const { data } = await api.get("/applications", { params });
      setApps(data);
    } catch (e) { toast.error("Failed to load applications"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadDashboard(); loadApps(); }, []);
  useEffect(() => { loadApps(); /* eslint-disable-next-line */ }, [priority]);

  const today = new Date().toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    let arr = apps;
    if (stageF !== "All") arr = arr.filter((a) => (a.stage || "New Application") === stageF);
    if (delayedOnly) arr = arr.filter((a) => (a.expected_completion && a.expected_completion < today) && !["Completed", "Delivered", "Rejected"].includes(a.stage));
    const t = q.trim().toLowerCase();
    if (t) {
      arr = arr.filter((a) =>
        (a.app_number || "").toLowerCase().includes(t) ||
        (a.app_no || "").toLowerCase().includes(t) ||
        (a.customer_name || "").toLowerCase().includes(t) ||
        (a.service_name || "").toLowerCase().includes(t) ||
        (a.portal_app_no || "").toLowerCase().includes(t),
      );
    }
    return arr;
  }, [apps, stageF, delayedOnly, q, today]);

  const clearFilters = () => { setStageF("All"); setPriority("All"); setQ(""); setDelayedOnly(false); };

  return (
    <div className="max-w-[1500px] mx-auto p-4 lg:p-6 space-y-6" data-testid="tracking-dashboard">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Rocket size={14} weight="fill" className="text-orange-500" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">Customer Service Tracking</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Admin Tracking Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Every application, every stage, every branch — filter and act in seconds.</p>
        </div>
        <Button className="bg-[#1e3a8a] hover:bg-[#1e2f6b] text-white gap-1.5 h-10" onClick={() => nav("/applications?create=1")} data-testid="tracking-new-app">
          <FileText size={14} /> New Application
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="tracking-kpi">
        {KPIS.map((k) => {
          const Ic = k.icon;
          const v = (kpi && kpi[k.key]) || 0;
          return (
            <Card key={k.key} className={`p-4 border-slate-200 ${k.key === "delayed" && v > 0 ? "ring-2 ring-red-300" : ""}`}>
              <div className={`w-9 h-9 rounded-xl ${k.bg} flex items-center justify-center mb-2`}>
                <Ic size={18} weight="duotone" className={k.tone} />
              </div>
              <div className="text-2xl font-bold text-slate-900">{v.toLocaleString("en-IN")}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-0.5">{k.label}</div>
            </Card>
          );
        })}
      </div>

      {/* Per-stage strip */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500 mb-2 flex items-center gap-2">
          <Funnel size={12} /> Filter by stage · click any chip
        </div>
        <div className="flex flex-wrap gap-2" data-testid="tracking-stage-chips">
          <button
            onClick={() => setStageF("All")}
            data-testid="chip-All"
            className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${stageF === "All" ? "bg-[#1e3a8a] text-white border-[#1e3a8a]" : "bg-white text-slate-700 border-slate-200 hover:border-[#1e3a8a]"}`}
          >
            All ({apps.length})
          </button>
          {ALL_STAGES.map((s) => {
            const entry = perStage.find((x) => x.stage === s);
            const n = entry?.count || 0;
            return (
              <button
                key={s}
                onClick={() => setStageF(s)}
                data-testid={`chip-${s.replace(/\s+/g, "-")}`}
                className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${stageF === s ? "bg-[#1e3a8a] text-white border-[#1e3a8a]" : `${stageColor(s)} hover:opacity-80`}`}
              >
                {s} <span className="opacity-70 ml-1">({n})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Toolbar */}
      <Card className="p-3 border-slate-200 flex flex-wrap items-center gap-2" data-testid="tracking-filters">
        <div className="flex items-center gap-2 flex-1 min-w-[240px] max-w-md bg-slate-50 border border-slate-200 rounded-full pl-4 pr-2 py-1">
          <MagnifyingGlass size={16} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 py-1.5 outline-none bg-transparent text-sm" placeholder="Search App No · Customer · Service · Portal Ref" data-testid="tracking-search" />
        </div>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="h-9 w-32" data-testid="filter-priority"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="All">All priorities</SelectItem>
            <SelectItem value="Normal">Normal</SelectItem>
            <SelectItem value="Urgent">Urgent</SelectItem>
            <SelectItem value="Emergency">Emergency</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => setDelayedOnly(!delayedOnly)}
          data-testid="filter-delayed"
          className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold border transition-colors ${delayedOnly ? "bg-red-100 text-red-700 border-red-300" : "bg-white text-slate-700 border-slate-200 hover:border-red-300"}`}
        >
          <XCircle size={12} /> Delayed only
        </button>
        <div className="ml-auto text-xs text-slate-500 flex items-center gap-2">
          <span className="font-semibold text-slate-900">{filtered.length}</span> results
          {(stageF !== "All" || priority !== "All" || delayedOnly || q) && (
            <button className="text-[#1e3a8a] hover:underline font-semibold" onClick={clearFilters} data-testid="clear-filters">Clear</button>
          )}
        </div>
      </Card>

      {/* Applications list */}
      <Card className="p-0 border-slate-200 overflow-hidden" data-testid="tracking-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">App No.</th>
                <th className="text-left px-3 py-3 font-semibold">Customer</th>
                <th className="text-left px-3 py-3 font-semibold">Service</th>
                <th className="text-left px-3 py-3 font-semibold">Stage</th>
                <th className="text-left px-3 py-3 font-semibold">Priority</th>
                <th className="text-left px-3 py-3 font-semibold">Payment</th>
                <th className="text-right px-3 py-3 font-semibold">Total</th>
                <th className="text-left px-3 py-3 font-semibold">Expected</th>
                <th className="text-right px-3 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400 text-sm">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-14 text-slate-400 text-sm">No applications match your filters.</td></tr>
              )}
              {filtered.map((a) => {
                const overdue = a.expected_completion && a.expected_completion < today && !["Completed", "Delivered", "Rejected"].includes(a.stage);
                return (
                  <tr key={a.id} data-testid={`row-${a.id}`} className="hover:bg-orange-50/40">
                    <td className="px-4 py-3 font-mono text-xs">
                      <div className="font-semibold text-slate-900">{a.app_number || a.app_no}</div>
                      <div className="text-[10px] text-slate-400">{a.app_no}</div>
                    </td>
                    <td className="px-3 py-3">{a.customer_name}</td>
                    <td className="px-3 py-3">{a.service_name}</td>
                    <td className="px-3 py-3">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${stageColor(a.stage || "New Application")}`}>
                        {a.stage || "New Application"}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-medium ${a.priority === "Emergency" ? "text-red-700 border-red-300 bg-red-50" : a.priority === "Urgent" ? "text-amber-700 border-amber-300 bg-amber-50" : "text-slate-700 border-slate-300 bg-slate-50"}`}>
                        {a.priority || "Normal"}
                      </Badge>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-[11px] font-semibold ${a.payment_status === "Paid" ? "text-emerald-700" : "text-amber-700"}`}>{a.payment_status}</span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono">{inr(a.total)}</td>
                    <td className={`px-3 py-3 text-xs ${overdue ? "text-red-700 font-semibold" : "text-slate-600"}`}>
                      {a.expected_completion || "—"}
                      {overdue && <div className="text-[10px] text-red-500 font-normal">Overdue</div>}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Link to={`/applications?id=${a.id}`} data-testid={`open-${a.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#1e3a8a] hover:underline">
                        Open <ArrowRight size={11} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
